import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import {
  Search, ShieldCheck, Clock, Star, ArrowRight,
  Sparkles, Snowflake, Bug, Wrench, Hammer, Paintbrush, Scissors,
  Heart, Cpu, Smartphone, Sprout, ClipboardCheck, Lamp, Building2, Shirt,
  Car, Droplets, Armchair, Zap, Store
} from 'lucide-react';

const ICON_MAP = {
  Snowflake, Sparkles, Bug, Wrench, Hammer, Paintbrush, Scissors,
  Heart, Cpu, Smartphone, Sprout, ClipboardCheck, Lamp, Building2, Shirt,
  Car, Droplets, Armchair, Zap, ShieldCheck, Store,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [proCategories, setProCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, p, pc] = await Promise.all([
          fetch(`${API}/services/categories`).then(r => r.json()),
          fetch(`${API}/services/providers/top?limit=8`).then(r => r.json()),
          fetch(`${API}/providers/categories`).then(r => r.json()).catch(() => []),
        ]);
        setCategories(c.categories || []);
        setProviders(p.providers || []);
        setProCategories(Array.isArray(pc) ? pc : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const proCards = proCategories.filter(c => c.thumb).map(c => ({
    slug: c.slug, name: c.label, tagline: c.description, icon: 'Store',
    color: '#0F172A', thumb: c.thumb, service_count: c.count,
    href: `/professionals/${c.slug}`, isPro: true,
  }));
  const homeCards = categories.map(c => ({ ...c, href: `/services/${c.slug}` }));
  const allCategories = [...homeCards, ...proCards];

  const filtered = allCategories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.tagline || '').toLowerCase().includes(search.toLowerCase())
  );

  const featured = categories.slice(0, 4);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="services-page">
      <Seo
        title="Home Services in 24+ Cities · AC, Cleaning, Salon, Electrician & More"
        description="Book trusted professionals for AC service, deep cleaning, electrician, plumber, salon, pest control, painting, and 14+ more home services. Doorstep, on-time, verified pros."
        path="/services"
      />

      {/* HERO */}
      <section className="relative pt-16 pb-20 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #D4AF37 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="container-luxury relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/15 text-[#D4AF37] rounded-full text-sm font-medium mb-5 border border-[#D4AF37]/30">
                <Sparkles className="h-4 w-4" /> Home Services by Bharat Estate
              </span>
              <h1 className="font-['Plus_Jakarta_Sans'] text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
                Trusted home services, <span className="text-[#D4AF37]">at your doorstep.</span>
              </h1>
              <p className="text-white/75 text-lg md:text-xl leading-relaxed max-w-2xl mb-8">
                AC repair, deep cleaning, salon, electrician, plumber, painting & 15+ more.
                Verified pros. Transparent prices. On-time, every time.
              </p>

              <div className="bg-white rounded-sm shadow-2xl p-2 flex items-center gap-2 max-w-2xl" data-testid="services-search-bar">
                <Search className="h-5 w-5 text-muted-foreground ml-3" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for AC service, cleaning, salon..."
                  className="flex-1 bg-transparent text-[#0F172A] outline-none px-2 py-3 text-base"
                  data-testid="services-search-input"
                />
                <Button className="bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold px-6 py-5">
                  Search
                </Button>
              </div>

              <div className="flex flex-wrap gap-6 mt-10 text-sm">
                {[
                  { i: ShieldCheck, t: 'Verified Pros' },
                  { i: Clock, t: 'On-time Promise' },
                  { i: Star, t: '4.8★ Avg Rating' },
                  { i: Sparkles, t: '100% Safe' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/80">
                    <b.i className="h-4 w-4 text-[#D4AF37]" /> {b.t}
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:grid grid-cols-2 gap-3">
              {featured.map((c) => {
                const Ic = ICON_MAP[c.icon] || Sparkles;
                return (
                  <Link
                    key={c.slug}
                    to={`/services/${c.slug}`}
                    className="group bg-white/5 backdrop-blur border border-white/10 rounded-sm p-5 hover:bg-white/10 transition-all"
                  >
                    <div className="h-10 w-10 rounded-sm flex items-center justify-center mb-3" style={{ backgroundColor: `${c.color}25`, color: c.color }}>
                      <Ic className="h-5 w-5" />
                    </div>
                    <p className="font-semibold text-sm mb-1">{c.name}</p>
                    <p className="text-xs text-white/60">Book now</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PACKERS & MOVERS BANNER */}
      <section className="py-8 bg-[#D4AF37]/10 border-y border-[#D4AF37]/30">
        <div className="container-luxury flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-[#0F172A] flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">🚚</span>
            </div>
            <div>
              <p className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] leading-tight">Moving home? Get an instant Packers & Movers quote.</p>
              <p className="text-sm text-muted-foreground">Local + intercity · Insurance included · 4.8★ avg rating</p>
            </div>
          </div>
          <Link to="/packers-movers">
            <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-6 py-5 font-semibold" data-testid="services-packers-cta">
              Get Quote in 30s <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CATEGORIES GRID */}
      <section className="py-20">
        <div className="container-luxury">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">All Services</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] leading-tight">What can we do for you today?</h2>
            </div>
            <p className="text-sm text-muted-foreground">{filtered.length} of {allCategories.length} categories</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-56 bg-white rounded-sm animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {filtered.map((cat) => {
                const Ic = ICON_MAP[cat.icon] || Sparkles;
                return (
                  <Link
                    key={cat.slug + (cat.isPro ? '-pro' : '')}
                    to={cat.href}
                    className="group bg-white rounded-sm border border-border/50 overflow-hidden hover:shadow-xl hover:border-[#D4AF37]/50 transition-all"
                    data-testid={`category-card-${cat.slug}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={cat.thumb} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 h-10 w-10 rounded-sm flex items-center justify-center shadow-md" style={{ backgroundColor: cat.color, color: '#fff' }}>
                        <Ic className="h-5 w-5" />
                      </div>
                      {cat.isPro && <div className="absolute top-3 right-3 bg-[#D4AF37] text-[#0F172A] text-[10px] font-bold px-2 py-1 rounded-sm uppercase tracking-wide">Directory</div>}
                      <div className="absolute bottom-3 left-3 right-3 text-white">
                        <p className="text-xs uppercase tracking-wider opacity-80">{cat.service_count ? `${cat.service_count} ${cat.isPro ? 'listed' : 'services'}` : (cat.isPro ? 'Browse' : 'Services')}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-[#0F172A] text-base mb-1 group-hover:text-[#D4AF37] transition-colors leading-tight">{cat.name}</h3>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{cat.tagline}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#0F172A]">{cat.isPro ? 'View providers' : 'View services'}</p>
                        <ArrowRight className="h-4 w-4 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* TOP PROFESSIONALS */}
      <section className="py-20 bg-white">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">The People Behind Your Service</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">Top-rated pros, ready to help.</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Background-verified, trained on Bharat Estate SOPs, and rated by real customers.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {providers.map((p) => (
              <div key={p.provider_id} className="bg-[#F9F9F7] rounded-sm p-5 text-center hover:shadow-md transition-shadow" data-testid={`provider-card-${p.provider_id}`}>
                <img src={p.photo} alt={p.name} className="h-20 w-20 rounded-full object-cover mx-auto mb-3 ring-2 ring-[#D4AF37]/50" />
                <h4 className="font-semibold text-[#0F172A]">{p.name}</h4>
                <p className="text-xs text-muted-foreground mt-0.5 capitalize">{p.category_slug.replace(/-/g, ' ')}</p>
                <div className="flex items-center justify-center gap-3 mt-3 text-xs">
                  <span className="flex items-center gap-1 text-[#0F172A] font-semibold">
                    <Star className="h-3 w-3 fill-[#D4AF37] text-[#D4AF37]" /> {p.rating}
                  </span>
                  <span className="text-muted-foreground">{p.jobs_done} jobs</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{p.experience_years} yrs exp · {p.city}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/become-a-pro">
              <Button variant="outline" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white px-6 py-5" data-testid="become-a-pro-cta">
                Are you a pro? Join Bharat Estate <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20">
        <div className="container-luxury">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">How it works</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">Three steps to a job well done.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: 1, h: 'Choose a service', b: 'Browse 21 categories, pick what you need, add to cart.' },
              { n: 2, h: 'Book a slot', b: 'Pick a date & time. Our pro calls to confirm — pay only after the job is done.' },
              { n: 3, h: 'Get it done', b: 'Verified pro arrives. You rate. Done.' },
            ].map((s) => (
              <div key={s.n} className="bg-white p-7 rounded-sm border border-border/50 text-center">
                <div className="h-12 w-12 rounded-full bg-[#0F172A] text-[#D4AF37] flex items-center justify-center mx-auto mb-4 font-['Plus_Jakarta_Sans'] text-2xl font-bold">{s.n}</div>
                <h3 className="font-semibold text-[#0F172A] text-lg mb-2">{s.h}</h3>
                <p className="text-sm text-muted-foreground">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#D4AF37]">
        <div className="container-luxury flex flex-col md:flex-row items-center justify-between gap-6 text-[#0F172A]">
          <div>
            <p className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold leading-tight">Need something now? We're on the way.</p>
            <p className="text-sm mt-1 opacity-80">Same-day availability in 24+ cities.</p>
          </div>
          <Link to="/services/ac-service-repair">
            <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-7 py-6 font-semibold" data-testid="services-cta-book">
              Book a service <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
